/**
 * Utility functions for normalizing store and warehouse location records from the backend
 */

/**
 * Normalize a raw location record into a standardized frontend object.
 *
 * @param {object} item - Raw backend record
 * @param {number} index - Index for fallback ID / order
 * @returns {object} Normalized location object
 */
export function formatLocationItem(item, index = 0) {
    if (!item || typeof item !== 'object') return null;

    const rawId = item._id || item.id || `loc-${index}`;
    const name = (item.name || item.location_name || item.title || 'Rahber Corporation').trim();

    // Type normalization (Physical Store vs Warehouse vs Store & Warehouse)
    const rawType = String(item.type || item.location_type || 'store').trim().toLowerCase();
    let typeLabel = 'Physical Store';
    let typeBadge = 'store';

    if (
        (rawType.includes('store') || rawType.includes('showroom')) &&
        rawType.includes('warehouse')
    ) {
        typeLabel = 'Store & Warehouse';
        typeBadge = 'store_warehouse';
    } else if (rawType.includes('warehouse')) {
        typeLabel = 'Warehouse';
        typeBadge = 'warehouse';
    } else if (rawType.includes('showroom')) {
        typeLabel = 'Showroom';
        typeBadge = 'showroom';
    } else if (rawType.includes('outlet') || rawType.includes('shop') || rawType.includes('store')) {
        typeLabel = 'Physical Store';
        typeBadge = 'store';
    }

    const city = (item.city || '').trim();
    const area = (item.area || '').trim();
    const address = (item.address || item.complete_address || item.full_address || '').trim();

    // Build full display address with area and city if not already included
    let fullAddressDisplay = address;
    if (area && address && !address.toLowerCase().includes(area.toLowerCase())) {
        fullAddressDisplay = `${area}, ${fullAddressDisplay}`;
    }
    if (city && fullAddressDisplay && !fullAddressDisplay.toLowerCase().includes(city.toLowerCase())) {
        fullAddressDisplay = `${fullAddressDisplay}, ${city}`;
    }

    // Contact numbers parsing (handles array, comma-separated string, or single string)
    let contactNumbers = [];
    const rawPhones = item.contact_number || item.phone || item.phones || item.phone_numbers;
    if (Array.isArray(rawPhones)) {
        contactNumbers = rawPhones.map((p) => {
            if (typeof p === 'object' && p !== null) {
                return {
                    display: p.display || p.number || p.tel || '',
                    tel: (p.tel || p.number || p.display || '').replace(/[^0-9+]/g, '')
                };
            }
            const str = String(p).trim();
            return {
                display: str,
                tel: str.replace(/[^0-9+]/g, '')
            };
        }).filter(p => p.display);
    } else if (typeof rawPhones === 'string' && rawPhones.trim()) {
        contactNumbers = rawPhones
            .split(/[,;/]+/)
            .map((s) => s.trim())
            .filter(Boolean)
            .map((str) => ({
                display: str,
                tel: str.replace(/[^0-9+]/g, '')
            }));
    }

    // Google Maps and directions URL resolution
    const rawMapUrl = (
        item.google_maps_url ||
        item.map_url ||
        item.directions_url ||
        item.location_link ||
        ''
    ).trim();

    const rawEmbedUrl = (
        item.embed_map_url ||
        item.embed_url ||
        ''
    ).trim();

    let googleMapsUrl = rawMapUrl;
    if (!googleMapsUrl) {
        const queryTarget = name && fullAddressDisplay ? `${name}, ${fullAddressDisplay}` : (fullAddressDisplay || address || name);
        googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(queryTarget)}`;
    }

    let embedMapUrl = rawEmbedUrl;
    if (!embedMapUrl) {
        if (rawMapUrl && (rawMapUrl.includes('output=embed') || rawMapUrl.includes('/embed'))) {
            embedMapUrl = rawMapUrl;
        } else {
            const queryTarget = name && fullAddressDisplay ? `${name}, ${fullAddressDisplay}` : (fullAddressDisplay || address || name);
            embedMapUrl = `https://maps.google.com/maps?q=${encodeURIComponent(queryTarget)}&t=&z=16&ie=UTF8&iwloc=&output=embed`;
        }
    }

    // Opening and closing hours
    const openingHours = (item.opening_hours || item.hours || item.timings || '').trim();

    // Additional info / description
    const description = (item.description || item.additional_info || item.notes || '').trim();

    // Display order
    const displayOrder = Number.isFinite(Number(item.display_order ?? item.displayOrder ?? item.order))
        ? Number(item.display_order ?? item.displayOrder ?? item.order)
        : index;

    // Active status
    const isActive =
        item.is_active !== false &&
        item.isActive !== false &&
        item.status !== 'inactive' &&
        item.is_published !== false;

    return {
        _id: rawId,
        id: String(rawId),
        rawId,
        name,
        type: rawType,
        typeLabel,
        typeBadge,
        address,
        area,
        city,
        fullAddressDisplay: fullAddressDisplay || address || name,
        contactNumbers,
        googleMapsUrl,
        embedMapUrl,
        openingHours,
        description,
        displayOrder,
        isActive,
        raw: item,
    };
}
